import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useTemplates, useApplyTemplate } from '@/hooks/useSettings'
import { Zap, Target, Gauge, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface QuickTemplatesProps {
  kbId: string
}

export function QuickTemplates({ kbId }: QuickTemplatesProps) {
  const { data: templates } = useTemplates()
  const applyTemplate = useApplyTemplate(kbId)
  const { toast } = useToast()

  const handleApplyTemplate = async (templateKey: string) => {
    try {
      await applyTemplate.mutateAsync(templateKey)
      toast({
        title: 'Template applied',
        description: `${templateKey} template has been applied successfully`,
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to apply template',
        variant: 'destructive',
      })
    }
  }

  const templateIcons: Record<string, any> = {
    standard: Target,
    precise: Zap,
    fast: Gauge,
  }

  const templateKeys = templates ? Object.keys(templates) : ['standard', 'precise', 'fast']

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" />
          Quick Setup Templates
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {templateKeys.map((key) => {
            const template = templates?.[key] || {
              name: key.charAt(0).toUpperCase() + key.slice(1),
              description: `${key} configuration preset`,
            }
            const Icon = templateIcons[key] || Target

            return (
              <Card key={key} className="hover:border-primary transition-colors">
                <CardContent className="p-4">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">{template.name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground flex-grow mb-4">
                      {template.description}
                    </p>
                    <Button
                      size="sm"
                      className="w-full"
                      onClick={() => handleApplyTemplate(key)}
                      disabled={applyTemplate.isPending}
                    >
                      {applyTemplate.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Applying...
                        </>
                      ) : (
                        'Apply'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
        <div className="mt-4 pt-4 border-t text-center text-sm text-muted-foreground">
          OR configure manually below
        </div>
      </CardContent>
    </Card>
  )
}
